/**
 * Session 狀態（Zustand + localStorage persist）。
 *
 * 兩階段 JWT：
 *  - sessionJwt：login 後拿到，用於 /auth/* 與 /me。platform_admin 的 session token
 *    本身即帶 is_platform_admin，可直接打業務 API（無需 switch-tenant）。
 *  - tenantJwt：switch-tenant 後拿到，一般 user 的業務 API 用它。
 *
 * 安全權衡：token 存 localStorage 有 XSS 風險，但本系統為內部 B2B、無 refresh token，
 * AntD 受控渲染降低風險面，暫接受。未來可評估改 httpOnly cookie（需後端配合）。
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Role } from '@bom/shared';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  is_platform_admin: boolean;
  must_change_password?: boolean;
}

export interface CurrentTenant {
  id: string;
  name: string;
  code: string;
}

interface SessionState {
  sessionJwt: string | null;
  sessionExpiresAt: number | null; // epoch seconds
  user: SessionUser | null;

  tenantJwt: string | null;
  tenantExpiresAt: number | null;
  currentTenant: CurrentTenant | null;
  role: Role | null;

  setSession: (jwt: string, expiresAt: number, user: SessionUser) => void;
  clearMustChangePassword: () => void;
  setTenant: (jwt: string, expiresAt: number, tenant: CurrentTenant, role: Role) => void;
  /** 平台管理員「進入」產品：用 platform token 跨產品（不簽 tenant_jwt、role 留 null） */
  enterTenantAsPlatform: (tenant: CurrentTenant) => void;
  clearTenant: () => void;
  logout: () => void;
}

const empty = {
  sessionJwt: null,
  sessionExpiresAt: null,
  user: null,
  tenantJwt: null,
  tenantExpiresAt: null,
  currentTenant: null,
  role: null,
} as const;

export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      ...empty,
      setSession: (jwt, expiresAt, user) =>
        set({ sessionJwt: jwt, sessionExpiresAt: expiresAt, user }),
      clearMustChangePassword: () =>
        set((s) => (s.user ? { user: { ...s.user, must_change_password: false } } : {})),
      setTenant: (jwt, expiresAt, tenant, role) =>
        set({
          tenantJwt: jwt,
          tenantExpiresAt: expiresAt,
          currentTenant: tenant,
          role,
        }),
      enterTenantAsPlatform: (tenant) =>
        set({ tenantJwt: null, tenantExpiresAt: null, currentTenant: tenant, role: null }),
      clearTenant: () =>
        set({ tenantJwt: null, tenantExpiresAt: null, currentTenant: null, role: null }),
      logout: () => set({ ...empty }),
    }),
    { name: 'bom-session' },
  ),
);

/** 非 React 環境（如 apiClient）讀寫 store 用 */
export const sessionStore = useSessionStore;
